import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserProfile } from '../../contexts/UserContext';
import { isExpired as checkExpired } from '../../services/adminService';

/**
 * SubscriptionGate
 * 
 * Envolve o conteúdo do dashboard.
 * - Super Admin: sempre liberado (sem plano necessário)
 * - Outros: bloqueia quando não tem plano ou plano expirado
 * 
 * Uso: <SubscriptionGate>{children}</SubscriptionGate>
 */
export default function SubscriptionGate({ children }) {
  const navigate = useNavigate();
  const { profile, subscription, isSuperAdmin, loading } = useUserProfile();

  // Enquanto carrega, não bloqueia
  if (loading) return children;

  // Super Admin nunca é bloqueado
  if (isSuperAdmin) return children;

  // Se não tem perfil ainda, deixa passar (auth cuida disso)
  if (!profile) return children;

  // Verificar se tem subscription válida
  const expired = !subscription || checkExpired(subscription);

  if (!expired) return children;

  // ── Tela de bloqueio ──
  const planoNome = subscription?.planoNome || 'Teste Grátis';
  const isTrialExpired = !subscription || subscription?.planoId === 'trial';

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 'calc(100vh - 80px)',
      padding: 32,
      fontFamily: 'var(--font)',
    }}>
      <div style={{
        maxWidth: 520,
        width: '100%',
        textAlign: 'center',
        padding: '48px 32px',
        background: 'var(--white)',
        borderRadius: 'var(--radius)',
        border: '1px solid var(--border)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
      }}>
        {/* Icon */}
        <div style={{
          width: 80, height: 80, borderRadius: 20,
          background: isTrialExpired
            ? 'linear-gradient(135deg, #FEF3C7, #FDE68A)'
            : 'linear-gradient(135deg, #FEE2E2, #FECACA)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 24px', fontSize: '2.2rem',
        }}>
          {isTrialExpired ? '⏱️' : '🔒'}
        </div>

        {/* Title */}
        <h2 style={{
          fontSize: '1.4rem', fontWeight: 700,
          color: 'var(--text)', margin: '0 0 8px',
        }}>
          {isTrialExpired ? 'Seu período de teste expirou' : `Plano ${planoNome} expirado`}
        </h2>

        {/* Description */}
        <p style={{
          fontSize: '0.92rem', color: 'var(--text-muted)',
          lineHeight: 1.6, margin: '0 0 32px',
        }}>
          {isTrialExpired
            ? 'Seu teste grátis de 7 dias chegou ao fim. Para continuar usando o SimulaBank e acessar todas as funcionalidades, escolha um plano que se adapte ao seu negócio.'
            : 'Seu plano expirou e o acesso ao sistema foi temporariamente suspenso. Renove seu plano para retomar suas atividades.'
          }
        </p>

        {/* Features reminder */}
        <div style={{
          background: 'var(--surface)', borderRadius: 12,
          padding: '20px 24px', marginBottom: 28,
          textAlign: 'left',
        }}>
          <div style={{
            fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 12,
          }}>
            O que você terá com um plano ativo:
          </div>
          {[
            'Simulações de financiamento ilimitadas',
            'Gestão completa de clientes (CRM)',
            'Geração de PDFs profissionais',
            'Dashboard com métricas em tempo real',
            'Gestão de equipe e usuários',
          ].map((f, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '6px 0', fontSize: '0.82rem', color: 'var(--text)',
            }}>
              <span style={{ color: 'var(--accent)', fontSize: '0.75rem' }}>✓</span>
              {f}
            </div>
          ))}
        </div>

        {/* CTA Buttons */}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => navigate('/planos')}
            style={{
              padding: '14px 32px', borderRadius: 10,
              background: 'var(--accent)', color: 'var(--primary)',
              border: 'none', fontFamily: 'var(--font)',
              fontSize: '0.92rem', fontWeight: 700,
              cursor: 'pointer', transition: 'all 0.2s',
              boxShadow: '0 4px 14px rgba(0,214,143,0.25)',
            }}
            onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
          >
            Ver Planos
          </button>
          <button
            onClick={() => navigate('/contato')}
            style={{
              padding: '14px 24px', borderRadius: 10,
              background: 'transparent', color: 'var(--text-muted)',
              border: '1.5px solid var(--border)', fontFamily: 'var(--font)',
              fontSize: '0.88rem', fontWeight: 600,
              cursor: 'pointer', transition: 'all 0.2s',
            }}
          >
            Falar com suporte
          </button>
        </div>

        {/* Note */}
        <p style={{
          fontSize: '0.72rem', color: 'var(--text-muted)',
          marginTop: 20, fontStyle: 'italic',
        }}>
          Seus dados estão seguros e serão restaurados ao ativar um novo plano.
        </p>
      </div>
    </div>
  );
}
